"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  ArrowLeftRight,
  Check,
  CheckCircle2,
  Copy,
  Database,
  FileText,
  MessageSquare,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { postWithAuth } from "@/lib/api-helpers";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { BrandIcon } from "@/components/tailwind/ui/BrandIcon";
import { integrationKeys } from "@/hooks/integrations";
import type { McpIntegrationTokenResponse } from "@/hooks/integrations";

/**
 * Notion-style "Connect Claude" flow for the Closot MCP server.
 *
 * Connect mode opens on a method chooser:
 *   - "Connect through claude.ai" (recommended) — the remote-MCP OAuth dance:
 *     add the Closot connector URL in claude.ai once, then Claude redirects
 *     to Closot's consent page (/mcp/authorize) for sign-in + workspace
 *     selection and back. No tokens change hands by hand.
 *   - "Use a token instead" — consent screen ("signing as" + access summary)
 *     → "Allow access" mints a token via POST /api/mcp-integrations →
 *     one-time token reveal with Claude Desktop / Claude Code setup.
 *
 * Rotate mode (pass `rotateIntegration`): in-modal confirm →
 * POST /api/mcp-integrations/:id/rotate → the same reveal step.
 *
 * The raw token is returned exactly once by the backend, so the modal is
 * strict about dismissal: while a request is in flight NOTHING closes it
 * (the old token may already be invalidated server-side), and on the reveal
 * step only the explicit X / Done buttons close it — Escape and backdrop
 * clicks are ignored.
 *
 * Mount this component only while the flow is open (`{modal && <McpConnectModal …>}`).
 * A fresh mount per open means state initializers run each time — no stale
 * step/token can flash from a previous session.
 *
 * Rendered in a portal; the `data-mcp-connect-modal` attribute is what keeps
 * clicks inside it from closing the parent SettingsModal (see its
 * outside-click handler).
 */

interface McpConnectModalProps {
  onClose: () => void;
  workspaceId: string;
  workspaceName?: string;
  /** When set, the modal runs the rotate flow for this integration instead of minting a new one. */
  rotateIntegration?: { id: string; name: string } | null;
}

/** What the minted token lets Claude do — mirrors the @closot/mcp tool surface. */
const ACCESS_ITEMS = [
  { icon: FileText, label: "Read, create, and edit pages" },
  { icon: Database, label: "Query and update databases" },
  { icon: Search, label: "Search across the workspace" },
  { icon: MessageSquare, label: "Read and write comments" },
] as const;

/**
 * API URL to pin in the generated client config, or null to omit it.
 *
 * Null when NEXT_PUBLIC_API_URL is unset — @closot/mcp already defaults to
 * https://api.closot.com, so omitting the line lets that default win. Emitting
 * a localhost fallback instead would OVERRIDE the correct default and bake a
 * dead URL into every user's config, and NEXT_PUBLIC_* is inlined at build
 * time, so a build that forgot the variable would ship that to everyone.
 */
function apiBaseUrl(): string | null {
  return process.env.NEXT_PUBLIC_API_URL?.trim() || null;
}

/**
 * Public URL of the hosted Closot MCP server (the claude.ai connector URL).
 * No localhost fallback on purpose: claude.ai can't reach localhost, the dev
 * default port (3000) is the Next app itself, and handing users a dead URL is
 * worse than telling the operator to configure NEXT_PUBLIC_MCP_URL.
 */
function mcpConnectorUrl(): string | null {
  const base = process.env.NEXT_PUBLIC_MCP_URL?.trim().replace(/\/+$/, "");
  if (!base) return null;
  return base.endsWith("/mcp") ? base : `${base}/mcp`;
}

/**
 * One-click install link into Claude's "Add custom connector" dialog, with the
 * name and server URL pre-filled — zero copy-paste. Claude still shows the
 * user the pre-filled values and requires them to confirm; the link grants
 * nothing on its own.
 *
 * Path note: connectors live under `/customize/connectors`. The older
 * `/settings/connectors` now renders a "Connectors have moved to Customize"
 * notice and never opens the dialog, so the modal param is silently lost.
 *
 * Returns null when NEXT_PUBLIC_MCP_URL is unconfigured, which disables the
 * button rather than sending users to a dialog pointing at nothing.
 */
function claudeConnectorDeepLink(): string | null {
  const url = mcpConnectorUrl();
  if (!url) return null;
  const params = new URLSearchParams({
    modal: "add-custom-connector",
    connectorName: "Closot",
    connectorUrl: url,
  });
  return `https://claude.ai/customize/connectors?${params.toString()}`;
}

function claudeDesktopConfig(token: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        closot: {
          command: "npx",
          args: ["-y", "@closot/mcp"],
          env: {
            CLOSOT_TOKEN: token,
            ...(apiBaseUrl() ? { CLOSOT_API_BASE_URL: apiBaseUrl() } : {}),
          },
        },
      },
    },
    null,
    2,
  );
}

function claudeCodeCommand(token: string): string {
  const base = apiBaseUrl();
  const apiFlag = base ? ` -e CLOSOT_API_BASE_URL=${base}` : "";
  return `claude mcp add closot -e CLOSOT_TOKEN=${token}${apiFlag} -- npx -y @closot/mcp`;
}

/** Hide broken external icons (CDN blocked / offline) instead of showing the broken-image glyph. */
function hideOnError(e: React.SyntheticEvent<HTMLImageElement>) {
  (e.target as HTMLImageElement).style.display = "none";
}

/** Small copy-to-clipboard button. Never claims "Copied" unless the copy actually happened. */
function CopyButton({ text, label }: { text: string; label?: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const t = window.setTimeout(() => setState("idle"), 1800);
    return () => window.clearTimeout(t);
  }, [state]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setState("copied");
    } catch {
      // Clipboard API unavailable (http, old browser) — fall back to a
      // temporary textarea. execCommand reports whether the copy succeeded.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } finally {
        document.body.removeChild(ta);
      }
      setState(ok ? "copied" : "failed");
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className={cn(
        "inline-flex items-center gap-1 px-2 h-7 rounded-md text-xs font-medium transition-colors flex-shrink-0",
        state === "failed"
          ? "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30"
          : "text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700",
      )}
    >
      {state === "copied" ? (
        <Check className="w-3.5 h-3.5 text-green-500" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
      {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : (label ?? "Copy")}
    </button>
  );
}

export default function McpConnectModal({
  onClose,
  workspaceId,
  workspaceName,
  rotateIntegration = null,
}: McpConnectModalProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Where the last mousedown/mouseup landed — a drag that starts or ends over
  // the dialog must not count as a backdrop click (the click event fires on
  // the common ancestor, i.e. the backdrop, in both drag directions).
  const mouseDownOnBackdropRef = useRef(false);
  const mouseUpOnBackdropRef = useRef(false);

  // Null when NEXT_PUBLIC_MCP_URL is unconfigured — i.e. no hosted MCP server
  // exists, so the one-click connector path isn't available at all.
  const connectorDeepLink = claudeConnectorDeepLink();

  // Skip straight to the token flow when there is no hosted server to point
  // Claude at: with @closot/mcp distributed as a package, minting a token and
  // pasting the Claude Desktop / Claude Code config IS the connect flow, so it
  // should be the primary screen rather than a footnote under a dead button.
  const [step, setStep] = useState<"method" | "authorize" | "confirm-rotate" | "token">(
    rotateIntegration ? "confirm-rotate" : connectorDeepLink ? "method" : "authorize",
  );
  const [token, setToken] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupTab, setSetupTab] = useState<"desktop" | "code">("desktop");

  // While the mint/rotate request is in flight the modal must not close:
  // the server may have already swapped the credential, and the response is
  // the only copy of the raw token that will ever exist.
  const canDismiss = !isBusy && step !== "token";

  // Move keyboard focus into the dialog on open AND on each step change —
  // step transitions unmount the previously focused button, which would
  // otherwise drop focus to document.body (outside the dialog).
  useEffect(() => {
    dialogRef.current?.focus();
  }, [step]);

  // Capture-phase listener, doing two jobs:
  //  - Escape: SettingsModal also closes on a document-level Escape, so we
  //    swallow the event before it reaches that handler. Never dismisses the
  //    one-time token reveal or an in-flight request.
  //  - Tab: trap focus inside the dialog. Both portals live on document.body,
  //    so without a trap Tab reaches the SettingsModal's controls underneath —
  //    including its close button, which would unmount this modal mid-flow.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (!isBusy && step !== "token") onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusables.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (!active || !dialog.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [isBusy, step, onClose]);

  const revealToken = (raw: string) => {
    // Reveal FIRST, then refresh the list in the background — awaiting the
    // refetch before showing the token widens the window where it can be lost.
    setToken(raw);
    setStep("token");
    void queryClient.invalidateQueries({ queryKey: integrationKeys.mcp(workspaceId) });
  };

  const runTokenRequest = async (url: string, body: Record<string, unknown>) => {
    if (isBusy) return;
    setIsBusy(true);
    setError(null);
    try {
      const res = await postWithAuth<McpIntegrationTokenResponse>(url, body);
      if (res && typeof res === "object" && "isError" in res && (res as { isError?: boolean }).isError) {
        setError((res as { message?: string }).message || "Something went wrong. Please try again.");
        return;
      }
      const data = res as McpIntegrationTokenResponse;
      if (!data?.token) {
        setError("Something went wrong. Please try again.");
        return;
      }
      revealToken(data.token);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleAllow = () =>
    runTokenRequest("/api/mcp-integrations", { workspaceId, name: "Claude" });

  const handleRotate = () => {
    if (!rotateIntegration) return;
    return runTokenRequest(`/api/mcp-integrations/${rotateIntegration.id}/rotate`, {});
  };

  const renderMethod = () => (
    <>
      {/* Icon handshake header */}
      <div className="flex items-center justify-center gap-3 pt-8 pb-4">
        <img src="/closot_logo.svg" alt="Closot" className="w-11 h-11 rounded-xl" onError={hideOnError} />
        <ArrowLeftRight className="w-4 h-4 text-zinc-400 dark:text-zinc-500" />
        <BrandIcon id="claude" size={44} alt="Claude" className="rounded-xl" />
      </div>

      <div className="px-8 text-center">
        <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
          Connect Claude to Closot
        </h2>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          One click — Claude opens with Closot pre-filled, then brings you back
          here to sign in and pick a workspace.
        </p>
      </div>

      {/* What happens after they click — set expectations, no manual steps. */}
      <div className="mx-8 mt-5 rounded-lg border border-zinc-200 dark:border-zinc-800 px-4 py-3.5 text-left">
        <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2">
          What happens next
        </div>
        <div className="space-y-2">
          {[
            "Claude opens with Closot already filled in — just confirm",
            "Sign in to Closot and choose a workspace",
            "Claude reconnects on its own — nothing to copy",
          ].map((line) => (
            <div key={line} className="flex items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              {line}
            </div>
          ))}
        </div>
      </div>

      {!connectorDeepLink && (
        <div className="mx-8 mt-4 flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 text-left">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          One-click connect isn&apos;t configured yet — the operator needs to host closot-mcp
          with <span className="font-mono">--transport http --oauth</span> and set{" "}
          <span className="font-mono">NEXT_PUBLIC_MCP_URL</span>. You can still connect with a
          token below.
        </div>
      )}

      <div className="flex flex-col items-center gap-2 px-8 py-5">
        <a
          href={connectorDeepLink ?? undefined}
          target="_blank"
          rel="noreferrer"
          aria-disabled={!connectorDeepLink}
          onClick={(e) => {
            if (!connectorDeepLink) e.preventDefault();
          }}
          className={cn(
            "w-full h-9 rounded-md text-sm font-medium transition-colors flex items-center justify-center",
            connectorDeepLink
              ? "text-white bg-blue-500 hover:bg-blue-600"
              : "text-zinc-400 dark:text-zinc-500 bg-zinc-100 dark:bg-zinc-800 cursor-not-allowed",
          )}
        >
          Connect with Claude
        </a>
        <button
          type="button"
          onClick={() => setStep("authorize")}
          className="text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:underline"
        >
          Set up manually with a token instead
        </button>
      </div>
    </>
  );

  const renderAuthorize = () => (
    <>
      {/* Icon handshake header */}
      <div className="flex items-center justify-center gap-3 pt-8 pb-4">
        <img src="/closot_logo.svg" alt="Closot" className="w-11 h-11 rounded-xl" onError={hideOnError} />
        <ArrowLeftRight className="w-4 h-4 text-zinc-400 dark:text-zinc-500" />
        <BrandIcon id="claude" size={44} alt="Claude" className="rounded-xl" />
      </div>

      <div className="px-8 text-center">
        <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
          Connect Claude to Closot
        </h2>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Claude is requesting access to{" "}
          <span className="font-medium text-zinc-700 dark:text-zinc-200">
            {workspaceName || "your workspace"}
          </span>
        </p>
      </div>

      {/* Signed-in account — the identity the token will act as */}
      <div className="mx-8 mt-5 flex items-center gap-3 rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2.5">
        {user?.image ? (
          <img src={user.image} alt={user?.name || "You"} className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center text-xs font-medium text-zinc-700 dark:text-zinc-200">
            {user?.name?.charAt(0).toUpperCase() || "U"}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
            {user?.name || "Signed-in user"}
          </div>
          {user?.email && (
            <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{user.email}</div>
          )}
        </div>
        <div className="ml-auto text-[10px] uppercase tracking-[0.04em] font-medium text-zinc-400 dark:text-zinc-500 whitespace-nowrap">
          Signing as
        </div>
      </div>

      {/* Access summary */}
      <div className="mx-8 mt-4 mb-2">
        <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2">
          Claude will be able to
        </div>
        <div className="space-y-2">
          {ACCESS_ITEMS.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-300">
              <Icon className="w-4 h-4 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
              {label}
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-zinc-400 dark:text-zinc-500 leading-relaxed">
          Claude acts with your permissions in this workspace only. You can revoke access at any
          time from Settings → Closot MCP.
        </p>
      </div>

      {error && (
        <div className="mx-8 mt-2 flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-600 dark:text-red-400">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 px-8 py-5">
        <button
          type="button"
          onClick={onClose}
          disabled={isBusy}
          className="h-8 px-3 rounded-md text-sm font-medium text-zinc-600 dark:text-zinc-300 hover-component disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleAllow()}
          disabled={isBusy || !workspaceId}
          className="h-8 px-4 rounded-md text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-60 transition-colors"
        >
          {isBusy ? "Connecting…" : "Allow access"}
        </button>
      </div>
    </>
  );

  const renderConfirmRotate = () => (
    <>
      <div className="flex flex-col items-center pt-8 px-8 text-center">
        <div className="w-10 h-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
        </div>
        <h2 className="mt-3 text-base font-semibold text-zinc-900 dark:text-zinc-100">
          Rotate this token?
        </h2>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
          A new token will be issued for{" "}
          <span className="font-medium text-zinc-700 dark:text-zinc-200">
            {rotateIntegration?.name}
          </span>{" "}
          and the current one stops working immediately. Clients using the old token must be
          updated.
        </p>
      </div>

      {error && (
        <div className="mx-8 mt-4 flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-600 dark:text-red-400">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 px-8 py-5">
        <button
          type="button"
          onClick={onClose}
          disabled={isBusy}
          className="h-8 px-3 rounded-md text-sm font-medium text-zinc-600 dark:text-zinc-300 hover-component disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleRotate()}
          disabled={isBusy}
          className="h-8 px-4 rounded-md text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-60 transition-colors"
        >
          {isBusy ? "Rotating…" : "Rotate token"}
        </button>
      </div>
    </>
  );

  const renderToken = () => {
    if (!token) return null;
    const snippet = setupTab === "desktop" ? claudeDesktopConfig(token) : claudeCodeCommand(token);
    return (
      <>
        <div className="flex flex-col items-center pt-8 px-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-green-500" />
          <h2 className="mt-3 text-base font-semibold text-zinc-900 dark:text-zinc-100">
            {rotateIntegration ? "Token rotated" : "Claude is connected"}
          </h2>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            Copy your integration token now — it is shown once and cannot be retrieved again.
          </p>
        </div>

        {/* One-time token */}
        <div className="mx-8 mt-4">
          <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">
            Integration token
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-3 py-2">
            <code className="text-xs font-mono text-zinc-800 dark:text-zinc-200 break-all leading-relaxed">
              {token}
            </code>
            <CopyButton text={token} />
          </div>
        </div>

        {/* Per-client setup */}
        <div className="mx-8 mt-4 mb-1">
          <div className="flex items-center gap-1 mb-2">
            {(
              [
                { id: "desktop", label: "Claude Desktop" },
                { id: "code", label: "Claude Code" },
              ] as const
            ).map((t) => (
              <button
                key={t.id}
                type="button"
                aria-pressed={setupTab === t.id}
                onClick={() => setSetupTab(t.id)}
                className={cn(
                  "h-7 px-2.5 rounded-full text-xs font-medium transition-colors",
                  setupTab === t.id
                    ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
                    : "text-zinc-500 dark:text-zinc-400 hover-component",
                )}
              >
                {t.label}
              </button>
            ))}
            <div className="ml-auto">
              <CopyButton text={snippet} />
            </div>
          </div>
          <pre className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-3 py-2.5 text-[11px] font-mono text-zinc-800 dark:text-zinc-200 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed max-h-44 overflow-y-auto clean-scrollbar">
            {snippet}
          </pre>
          <p className="mt-2 text-xs text-zinc-400 dark:text-zinc-500 leading-relaxed">
            {setupTab === "desktop" ? (
              <>
                Paste into{" "}
                <span className="font-mono">
                  ~/Library/Application Support/Claude/claude_desktop_config.json
                </span>{" "}
                (macOS) or <span className="font-mono">%APPDATA%\Claude\claude_desktop_config.json</span>{" "}
                (Windows), then restart Claude Desktop.
              </>
            ) : (
              <>Run this in your terminal, then start a new Claude Code session.</>
            )}
          </p>
        </div>

        <div className="flex items-center justify-end px-8 py-5">
          <button
            type="button"
            onClick={onClose}
            className="h-8 px-4 rounded-md text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 transition-colors"
          >
            Done
          </button>
        </div>
      </>
    );
  };

  return createPortal(
    <div
      data-mcp-connect-modal="true"
      className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center bg-black/50 dark:bg-black/70"
      onMouseDown={(e) => {
        mouseDownOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        mouseUpOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // Close only for a true backdrop click: press AND release both on the
        // backdrop. Drags in either direction (select text → release outside,
        // or press outside → release on the dialog) synthesize a click on this
        // ancestor and must not dismiss the flow. Never closes during an
        // in-flight request or on the one-time token reveal.
        if (
          e.target === e.currentTarget &&
          mouseDownOnBackdropRef.current &&
          mouseUpOnBackdropRef.current &&
          canDismiss
        ) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={
          rotateIntegration
            ? `Rotate token for ${rotateIntegration.name}`
            : "Connect Claude to Closot"
        }
        tabIndex={-1}
        className="relative bg-white dark:bg-zinc-900 shadow-2xl w-full md:w-[440px] md:max-w-[calc(100vw-32px)] rounded-t-2xl md:rounded-2xl max-h-[90dvh] overflow-y-auto clean-scrollbar focus:outline-none"
      >
        <button
          type="button"
          onClick={onClose}
          disabled={isBusy}
          aria-label="Close"
          className="absolute top-3 right-3 w-[22px] h-[22px] rounded-full flex items-center justify-center text-zinc-500 dark:text-zinc-400 hover-component disabled:opacity-50 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        {step === "method"
          ? renderMethod()
          : step === "authorize"
            ? renderAuthorize()
            : step === "confirm-rotate"
              ? renderConfirmRotate()
              : renderToken()}
      </div>
    </div>,
    document.body,
  );
}
